import React, { ReactNode, useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { useIntl } from 'react-intl';

import { GRID_SIZE, GRID_SIZE_ORDER } from '@modules/plp/plp.constants';
import { Artwork, Meta } from '@definitions/artworks.types';
import { PLP_VARIANT } from '@modules/plp';
import { renderWhenTrue } from '@utils/rendering';
import { Layout } from '@components/layout';

import { UpcomingGrid } from '../grid/upcomingGrid';
import { PastGrid } from '../grid/pastGrid';
import { CurrentGrid } from '../grid/currentGrid';

import { Container, MotionContainer } from './artistsDesignedProducts.styles';
import { ANIMATION_DURATION, ARTWORKS_GRID_TEST_ID, ARTWORK_POSITION_TYPE } from './artistsDesignedProducts.constants';

export interface ArtistDesignedProductsProps {
  artworks: Artwork[];
  gridSize: GRID_SIZE;
  initialGridSize?: GRID_SIZE;
  plp: PLP_VARIANT;
  groupingsIntro?: ReactNode;
  context: {
    meta: Meta;
    loading: boolean;
    fetchArtworks: (data: any) => Promise<any>;
    currentPage: number;
  };
}

export const ArtistDesignedProducts = ({
  artworks,
  gridSize,
  groupingsIntro,
  plp,
  initialGridSize,
  context: { meta, loading: itemsLoading, fetchArtworks, currentPage },
}: ArtistDesignedProductsProps) => {
  const [, setNewGridSize] = useState<GRID_SIZE | null>(null);
  const [oldGridSize, setOldGridSize] = useState<GRID_SIZE | null>(null);
  const [position, setPosition] = useState<ARTWORK_POSITION_TYPE>(ARTWORK_POSITION_TYPE.RELATIVE);

  const intl = useIntl();
  const currentSectionTitle = intl.formatMessage({ id: 'designedProducts.current', defaultMessage: 'Current:' });
  const upcomingSectionTitle = intl.formatMessage({ id: 'designedProducts.future', defaultMessage: 'Upcoming:' });
  const pastSectionTitle = intl.formatMessage({ id: 'designedProducts.past', defaultMessage: 'Past:' });

  const getCurrentArtWorks = (artworks: Artwork[]) => {
    return artworks.filter(
      (artwork) => dayjs(artwork.liveStartAt).isBefore(dayjs()) && dayjs(artwork.liveEndAt).isAfter(dayjs())
    );
  };

  const getPastArtworks = (artworks: Artwork[]) => {
    return artworks.filter((artwork) => dayjs(artwork.liveEndAt).isBefore(dayjs()));
  };

  const getUpcomingArtworks = (artworks: Artwork[]) => {
    return artworks.filter((artwork) => dayjs(artwork.liveStartAt).isAfter(dayjs()));
  };

  const currentArtworks = getCurrentArtWorks(artworks);
  const upcomingArtworks = getUpcomingArtworks(artworks);
  const pastArtworks = getPastArtworks(artworks);

  useEffect(() => {
    setTimeout(() => {
      setNewGridSize(gridSize);
    });
  }, [gridSize]);

  useEffect(() => {
    if (initialGridSize) {
      setTimeout(() => {
        setNewGridSize(initialGridSize);
      });
    }
  }, [initialGridSize]);

  const isReverse = gridSize && oldGridSize && GRID_SIZE_ORDER[gridSize] < GRID_SIZE_ORDER[oldGridSize];
  const motionProps = {
    initial: oldGridSize
      ? {
          scale: isReverse ? 2 : 0.5,
          opacity: 0,
        }
      : false,
    animate: {
      scale: 1,
      opacity: 1,
    },
    exit: {
      scale: isReverse ? 0.5 : 2,
      opacity: 0,
    },
    transition: { ease: 'linear', duration: ANIMATION_DURATION / 1000 },
    onAnimationComplete: () => {
      setPosition(ARTWORK_POSITION_TYPE.RELATIVE);
      setOldGridSize(gridSize);
    },
    onAnimationStart: () => {
      setPosition(ARTWORK_POSITION_TYPE.ABSOLUTE);
    },
  };

  const renderUpcomingArtworks = renderWhenTrue(() => (
    <MotionContainer key={`${GRID_SIZE.SMALL}-upcoming`} {...motionProps} position={position}>
      <UpcomingGrid artworks={upcomingArtworks} title={upcomingSectionTitle} />
    </MotionContainer>
  ));

  const renderPastArtworks = renderWhenTrue(() => (
    <MotionContainer key={`${GRID_SIZE.SMALL}-past`} {...motionProps} position={position}>
      <PastGrid artworks={pastArtworks} title={pastSectionTitle} />
    </MotionContainer>
  ));

  return (
    <Container data-testid={ARTWORKS_GRID_TEST_ID}>
      {groupingsIntro}
      <MotionContainer key={GRID_SIZE.MEDIUM} {...motionProps} position={position}>
        <CurrentGrid artworks={currentArtworks} title={currentSectionTitle} />
      </MotionContainer>
      {renderUpcomingArtworks(upcomingArtworks.length > 0)}
      {renderPastArtworks(pastArtworks.length > 0)}
    </Container>
  );
};
